import React, { ErrorInfo } from 'react'
import * as Sentry from '@sentry/browser'
import styled from 'styled-components'

import { Text } from 'components/Box'

const Container = styled.div`
  margin: 12px;
  text-align: center;
`

export const NotificationsErrorDisplay = () => {
  return (
    <Container>
      <Text fontWeight={600} color="text.dark.primary" mb={2}>
        There was an error loading this information
      </Text>
      <Text color="text.dark.secondary" mt={2}>
        We’re aware of the issue and will fix it as soon as possible. You can
        find more information on our{' '}
        <a href="https://status.sketch.com/">Status Page</a>.
      </Text>
    </Container>
  )
}

/**
 * ### Note:
 * Error boundaries do not catch errors for:
 * - Event handlers
 * - Asynchronous code (e.g. `setTimeout` or `requestAnimationFrame` callbacks)
 * - Server side rendering
 * - Errors thrown in the error boundary itself (rather than its children)
 * see more: https://reactjs.org/docs/error-boundaries.html#introducing-error-boundaries
 *
 * This means, that using ErrorBoundary we should NOT expect to cover GraphQL, network or all other async errors.
 */
class NotificationsErrorBoundary extends React.Component {
  state = { hasError: false }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    Sentry.withScope(scope => {
      scope.setExtra('info', info)
      Sentry.captureException(error)
    })
  }

  render() {
    if (this.state.hasError) {
      return <NotificationsErrorDisplay />
    }

    return this.props.children
  }
}

export default NotificationsErrorBoundary
